import { useQuery } from '@apollo/client';
import _ from 'lodash';
import useAppSelector from '../app/redux/useAppSelector';
import { gql } from '../graphql/generated/gql';

const AUTOCOMPLETE_QUERY = gql(`
  query InvitationAutocomplete {
    session: currentSession {
      organization {
        id
        externalUsers {
          name
          email
          avatar
        }
        invitations {
          id
          email
        }
        users(includeDeactivated: true) {
          id
          email
          deactivatedAt
          importedFrom
        }
      }
    }
  }
`);

export enum ExternalUserState {
  Invited = 'INVITED',
  Deactivated = 'DEACTIVATED',
  Teammate = 'TEAMMATE',
  Uninvited = 'UNINVITED',
}

type InvitationExternalUser = {
  state: ExternalUserState | undefined;
  name?: string | null | undefined;
  email: string;
  avatar?: string | null | undefined;
};

const useInvitationAutocompleteQuery = () => {
  const currentUser = useAppSelector((state) => state.session.user);
  const { data: { session } = {}, loading } = useQuery(AUTOCOMPLETE_QUERY);

  if (!session?.organization?.externalUsers) {
    return { loading };
  }

  const invitedEmails = new Set(
    _.compact(
      session?.organization?.invitations?.map(
        (invitation) => invitation?.email,
      ),
    ),
  );
  const deactivatedEmails = new Set(
    _.compact(
      session?.organization?.users
        ?.filter((user) => !!user?.deactivatedAt)
        .map((user) => user?.email),
    ),
  );
  const teammateEmails = new Set(
    _.compact(
      session?.organization?.users
        ?.filter((user) => !user?.deactivatedAt && !user?.importedFrom)
        .map((user) => user?.email),
    ),
  );

  const externalUsers = session.organization.externalUsers
    .map<InvitationExternalUser>((user) => {
      const email = user?.email;
      let state;
      if (email) {
        if (invitedEmails.has(email)) {
          state = ExternalUserState.Invited;
        } else if (deactivatedEmails.has(email)) {
          state = ExternalUserState.Deactivated;
        } else if (teammateEmails.has(email)) {
          state = ExternalUserState.Teammate;
        } else {
          state = ExternalUserState.Uninvited;
        }
      }
      return {
        ...user,
        state,
      };
    })
    .filter(
      ({ state, email }) =>
        state !== ExternalUserState.Deactivated && email !== currentUser.email,
    );

  return { loading, externalUsers };
};

export default useInvitationAutocompleteQuery;
